/**
 * O dashboard — a primeira tela depois do login (ISSUE-210).
 *
 * **ESTA TELA ERA INTEIRAMENTE MOCKADA.** Mostrava 12 agentes, 847 execuções e
 * 8 workflows, mais uma lista de execuções de agentes que não existem neste
 * produto. O dano não era o número errado: era o número **convincente**. Campo
 * vazio comunica "não sei"; um `847` com "+124 vs ontem" comunica "sei, e está
 * crescendo".
 *
 * **POR ISSO OS TRÊS ESTADOS SÃO A METADE QUE IMPORTA.** Um dashboard que, ao
 * falhar, mostra zero ou o último valor conhecido volta a afirmar o que não
 * sabe — é a mesma mentira com outra origem. Aqui, quando a leitura falha,
 * **nenhum número aparece**.
 */
import {
  AlertCircle,
  ArrowRight,
  Bot,
  CheckCircle,
  Clock,
  Plus,
  RefreshCw,
  TrendingUp,
  Upload,
  Workflow,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useResumoDoPainel, type Situacao } from '@/hooks/use-resumo-do-painel'
import { inteiro, numero } from '@/lib/formato'
import type {
  AgenteMaisUsado,
  Contagem,
  ContagemComNovos,
  ExecucaoRecente,
} from '@/schemas/dashboard'
import { useOrganizationStore } from '@/stores/organization-store'

type Cor = 'indigo' | 'emerald' | 'purple'

function cores(cor: Cor) {
  switch (cor) {
    case 'indigo':
      return { bg: 'bg-indigo-500/20', icon: 'text-indigo-400', text: 'text-indigo-400' }
    case 'emerald':
      return { bg: 'bg-emerald-500/20', icon: 'text-emerald-400', text: 'text-emerald-400' }
    case 'purple':
      return { bg: 'bg-purple-500/20', icon: 'text-purple-400', text: 'text-purple-400' }
  }
}

function IconeDoDesfecho({ status }: { status: string }) {
  if (status === 'success') return <CheckCircle className="w-5 h-5 text-emerald-400" />
  if (status === 'running' || status === 'pending')
    return <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
  return <AlertCircle className="w-5 h-5 text-red-400" />
}

function fundoDoDesfecho(status: string) {
  if (status === 'success') return 'bg-emerald-500/20'
  if (status === 'running' || status === 'pending') return 'bg-indigo-500/20'
  return 'bg-red-500/20'
}

/**
 * "há 3 min" — e, quando passa de um dia, a DATA NO FUSO DO PAINEL.
 *
 * **O FUSO VEM DO SERVIDOR E PRECISA CHEGAR ATÉ AQUI.** Ele atravessava o
 * contrato, o schema e o Zod e morria sem leitor: o servidor contava "hoje" em
 * São Paulo e a lista ao lado datava no fuso do navegador. Uma pessoa em Lisboa
 * veria o cartão dizer "3 execuções hoje" e a lista datá-las de ontem — que é
 * exatamente a divergência que o FR-004 existe para impedir. Achado na revisão
 * de código, e é a mesma classe do `painel_timezone` que o analyze pegou, um
 * nível acima.
 *
 * A diferença em minutos não depende de fuso; a data absoluta depende.
 */
function haQuantoTempo(
  instante: string,
  fuso: string,
  idioma: string,
  t: ReturnType<typeof useTranslation>['t']
): string {
  const minutos = Math.floor((Date.now() - new Date(instante).getTime()) / 60000)
  if (minutos < 1) return t('dashboard.now')
  if (minutos < 60) return t('dashboard.minAgo', { count: minutos })
  const horas = Math.floor(minutos / 60)
  if (horas < 24) return t('dashboard.hoursAgo', { count: horas })
  // **O IDIOMA VEM DO i18n, O FUSO VEM DO SERVIDOR.** A versão anterior cravava
  // `'pt-BR'` num produto que fala três idiomas: o fuso foi parametrizado e o
  // idioma não acompanhou. Achado na revisão de código.
  return new Date(instante).toLocaleDateString(idioma, { timeZone: fuso })
}

/** A cor da comparação SEGUE O SINAL, não o tema do cartão.
 *
 * O cartão de execuções é verde, e a cor do tema pintava a comparação inteira
 * de verde: `-40 vs ontem` aparecia como boa notícia. A cor é informação, e
 * informação errada é pior que cor nenhuma. Achado na revisão de código.
 *
 * Zero fica cinza de propósito: houve período anterior e o número não mudou —
 * não é alta nem queda, e pintá-lo de qualquer um dos dois afirmaria direção.
 */
function corDaVariacao(variacao: number | null | undefined): string {
  if (variacao === null || variacao === undefined || variacao === 0) return 'text-gray-500'
  return variacao > 0 ? 'text-emerald-400' : 'text-rose-400'
}

interface CartaoProps {
  situacao: Situacao
  /**
   * Só o total: o cartão desenha o número, e quem monta a frase debaixo dele é
   * quem chama. É o que permite os dois vocabulários — variação nas execuções,
   * contagem de novos no acervo — no mesmo cartão.
   */
  contagem: { total: number } | undefined
  rotulo: string
  icone: LucideIcon
  cor: Cor
  /** O texto da comparação, já traduzido. Ausente quando não há base. */
  comparacao?: string
  /**
   * O sinal da variação, que decide a COR da comparação — ver `corDaVariacao`.
   * Contagem de novos entra aqui como número positivo, e sai verde.
   */
  variacao?: number | null
  /** Marca o cartão para o teste poder afirmar QUAL número está em QUAL cartão. */
  nome: string
  /** O que dizer quando o número é zero DE VERDADE. */
  textoDoZero: string
  aoTentarDeNovo: () => void
}

function Cartao({
  situacao,
  contagem,
  rotulo,
  icone: Icone,
  cor,
  comparacao,
  variacao,
  nome,
  textoDoZero,
  aoTentarDeNovo,
}: CartaoProps) {
  const { t } = useTranslation('common')
  const c = cores(cor)

  return (
    <Card variant="glass" className="p-6" data-cartao={nome}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-gray-400 text-sm font-medium">{rotulo}</p>

          {/* BUSCANDO — com texto, não esqueleto mudo. O serviço escala a zero,
              então a espera é o caso comum na primeira visita do dia. */}
          {situacao === 'buscando' && (
            <p className="text-sm mt-3 text-gray-500 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              {t('dashboard.buscando')}
            </p>
          )}

          {/* FALHOU — NENHUM NÚMERO. Nem zero, nem o valor de antes. */}
          {situacao === 'falhou' && (
            <>
              <p className="text-sm mt-3 text-amber-400">{t('dashboard.naoConsegui')}</p>
              <Button
                variant="link"
                size="sm"
                className="px-0 h-auto mt-1 text-gray-400"
                onClick={aoTentarDeNovo}
              >
                {t('dashboard.tentarDeNovo')}
              </Button>
            </>
          )}

          {situacao === 'pronto' && contagem && (
            <>
              <p data-numero className="text-3xl font-bold mt-2 text-gray-100">
                {inteiro(contagem.total)}
              </p>
              {/* Zero de verdade é declarado; a comparação só aparece quando há
                  período anterior — `null` a omite, `0` não. */}
              {/* ZERO DECLARADO E A QUEDA JUNTO. Ontem 40, hoje 0 é a queda
                  mais informativa do painel, e a versão anterior mostrava só
                  "nada aconteceu hoje" — engolindo a informação que importa.
                  Achado na revisão de código. */}
              {contagem.total === 0 && (
                <p className="text-sm mt-1 text-gray-500">{textoDoZero}</p>
              )}
              {comparacao && (
                <p data-numero className={`text-sm mt-1 ${corDaVariacao(variacao)}`}>
                  {comparacao}
                </p>
              )}
            </>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.bg}`}>
          <Icone className={`w-6 h-6 ${c.icon}`} />
        </div>
      </div>
    </Card>
  )
}

export function DashboardPage() {
  const { t, i18n } = useTranslation('common')
  const { currentOrganization } = useOrganizationStore()
  const { situacao, dado, rebuscar } = useResumoDoPainel(currentOrganization?.id)

  /**
   * "+3 novos" — a frase dos cartões de agentes e workflows.
   *
   * **É CONTAGEM, NÃO COMPARAÇÃO**, e por isso não passa por `comparacao`. O
   * servidor mandava `variacao` aqui, calculada de um jeito que a impedia de
   * ser negativa: semana em que se apagou quatro agentes saía como "0 esta
   * semana". `0` agora não rende texto nenhum — não houve criação, e afirmar
   * isso em prosa só ocuparia a linha. Achado na revisão de código.
   */
  const novos = (c: ContagemComNovos | undefined): string | undefined => {
    if (!c || c.novos === 0) return undefined
    return t('dashboard.new', { count: c.novos })
  }

  const comparacao = (c: Contagem | undefined, chave: string): string | undefined => {
    // `null` é AUSÊNCIA DE PERÍODO ANTERIOR — a comparação some. `0` seria
    // "houve período e foi igual", que é outra afirmação.
    if (!c || c.variacao === null || c.variacao === undefined) return undefined
    // **O SINAL É CALCULADO, NÃO ESCRITO NO TEXTO.** O rótulo trazia um `+`
    // fixo, e queda de 14 saía como `+-14 vs ontem`. Achado na validação visual.
    const sinal = c.variacao > 0 ? '+' : ''
    // Sem `count`: as frases desta chave interpolam só `{{sinal}}`. Passar
    // `count` ligava a resolução de plural do i18next por engano — hoje ela cai
    // de volta na chave base, e no dia em que alguém criar `thisWeek_one` a
    // variação `+1` passaria a resolver outra string. Achado na revisão.
    return t(chave, { sinal: `${sinal}${inteiro(c.variacao)}` })
  }

  const recentes: ExecucaoRecente[] = dado?.recentes ?? []
  const maisUsados: AgenteMaisUsado[] = dado?.mais_usados ?? []

  return (
    // `data-numero` marca CADA VALOR que a tela afirma sobre o negócio — não a
    // região. Prosa com dígito ("últimos 7 dias") e o relógio da apuração ficam
    // de fora: a varredura que proíbe número inventado tem de distinguir valor
    // de texto, senão vira ruído e acaba afrouxada até não medir nada.
    <div className="space-y-6">
      {/* **TRÊS COLUNAS SÓ A PARTIR DE `lg`.** Em `md` (768px) a barra lateral
          come 256px e sobram ~155px por cartão: "não consegui ler este número"
          quebrava em quatro linhas e passava por baixo do ícone. Achado na
          validação visual. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <Cartao
          situacao={situacao}
          contagem={dado?.agentes}
          rotulo={t('dashboard.activeAgents')}
          icone={Bot}
          cor="indigo"
          comparacao={novos(dado?.agentes)}
          variacao={dado?.agentes.novos}
          nome="agentes"
          textoDoZero={t('dashboard.semAgentes')}
          aoTentarDeNovo={rebuscar}
        />
        <Cartao
          situacao={situacao}
          contagem={dado?.execucoes_hoje}
          rotulo={t('dashboard.executionsToday')}
          icone={Zap}
          cor="emerald"
          comparacao={comparacao(dado?.execucoes_hoje, 'dashboard.vsYesterday')}
          variacao={dado?.execucoes_hoje.variacao}
          nome="execucoes"
          textoDoZero={t('dashboard.semExecucoesHoje')}
          aoTentarDeNovo={rebuscar}
        />
        <Cartao
          situacao={situacao}
          contagem={dado?.workflows}
          rotulo={t('dashboard.workflows')}
          icone={Workflow}
          cor="purple"
          comparacao={novos(dado?.workflows)}
          variacao={dado?.workflows.novos}
          nome="workflows"
          textoDoZero={t('dashboard.semWorkflows')}
          aoTentarDeNovo={rebuscar}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card variant="glass" className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-100">
              {t('dashboard.recentExecutions')}
            </h2>
            <Button variant="link" size="sm" className="gap-1" asChild>
              <Link to="/executions">
                {t('dashboard.viewAll')} <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>

          {situacao === 'buscando' && (
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              {t('dashboard.buscando')}
            </p>
          )}
          {situacao === 'falhou' && (
            <p className="text-sm text-amber-400">
              {t('dashboard.naoConseguiLista')}
            </p>
          )}
          {situacao === 'pronto' && recentes.length === 0 && (
            <p className="text-sm text-gray-500">{t('dashboard.semExecucoesAinda')}</p>
          )}

          <div className="space-y-3">
            {situacao === 'pronto' &&
              recentes.map((e) => (
                <div
                  key={e.id}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 p-4 bg-white/5 rounded-xl min-w-0"
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${fundoDoDesfecho(e.status)}`}
                  >
                    <IconeDoDesfecho status={e.status} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-100 truncate">{e.agente}</p>
                    <p className="text-sm text-gray-500">{haQuantoTempo(e.em, dado!.fuso, i18n.language, t)}</p>
                  </div>
                  {e.tokens != null && (
                    // **A CONTAGEM DESCE PARA A PRÓPRIA LINHA NO CELULAR.** Em
                    // 375px ela disputava a largura com o nome do agente e o
                    // reduzia a "Atendi…" — sete caracteres, que não identificam
                    // agente nenhum. Achado na validação visual.
                    <div className="basis-full text-right sm:basis-auto sm:flex-shrink-0">
                      <p data-numero className="text-sm font-mono text-gray-400">
                        {inteiro(e.tokens)} {t('dashboard.tokens')}
                      </p>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </Card>

        <Card variant="glass" className="p-6">
          <h2 className="text-lg font-semibold text-gray-100 mb-6">
            {t('dashboard.quickActions')}
          </h2>
          <div className="space-y-3">
            {/* **OS TRÊS DESTINOS SÃO CONFERIDOS CONTRA O `App.tsx`**, por um
                teste que varre esta página inteira. Os três apontavam para
                rotas que não existem — `/agents/new`, `/workflows/new` e
                `/knowledge/upload` — e o catch-all devolvia à raiz em silêncio,
                que é o mesmo defeito que esta entrega existe para matar. Três
                voltas de revisão e a validação visual passaram por cima: foto
                mostra o botão desenhado, não para onde ele leva. */}
            <Button variant="action-indigo" className="w-full justify-start gap-3" asChild>
              <Link to="/agents/create">
                <Plus className="w-5 h-5" />
                <span>{t('dashboard.newAgent')}</span>
              </Link>
            </Button>
            <Button variant="action-purple" className="w-full justify-start gap-3" asChild>
              <Link to="/workflows?novo=1">
                <Workflow className="w-5 h-5" />
                <span>{t('dashboard.newWorkflow')}</span>
              </Link>
            </Button>
            <Button variant="action-emerald" className="w-full justify-start gap-3" asChild>
              <Link to="/knowledge-base">
                <Upload className="w-5 h-5" />
                <span>{t('dashboard.basesDeConhecimento')}</span>
              </Link>
            </Button>
            {/* O atalho de criar time SAIU junto com o item de menu: `/teams/new`
                não existe, e oferecer botão que não faz nada é o mesmo defeito
                que esta entrega mata. */}
          </div>
        </Card>
      </div>

      <Card variant="glass" className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-100">{t('dashboard.topAgents')}</h2>
          <TrendingUp className="w-5 h-5 text-emerald-400" />
        </div>

        {situacao === 'buscando' && (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            {t('dashboard.buscando')}
          </p>
        )}
        {situacao === 'falhou' && (
          <p className="text-sm text-amber-400">{t('dashboard.naoConseguiLista')}</p>
        )}
        {situacao === 'pronto' && maisUsados.length === 0 && (
          <p className="text-sm text-gray-500">{t('dashboard.semAgentesUsados')}</p>
        )}

        <div className="space-y-4">
          {situacao === 'pronto' &&
            maisUsados.map((a, i) => (
              <div key={a.agente_id} className="flex items-center gap-4 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-bold text-sm flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-100 truncate">{a.agente}</p>
                  <p data-numero className="text-sm text-gray-500">
                    {inteiro(a.execucoes)} {t('dashboard.executions')}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  {/* `null` é denominador zero: nada terminou. `0%` afirmaria
                      "nenhum sucesso" sobre agente que ainda está rodando. */}
                  {a.taxa_de_sucesso === null ? (
                    <p className="text-sm text-gray-500">{t('dashboard.semDesfecho')}</p>
                  ) : (
                    <>
                      <p data-numero className="text-sm font-medium text-emerald-400">
                        {numero(a.taxa_de_sucesso * 100)}%
                      </p>
                      <p className="text-xs text-gray-500">{t('dashboard.success')}</p>
                    </>
                  )}
                </div>
              </div>
            ))}
        </div>
      </Card>

      {dado && (
        // O INSTANTE SOZINHO NÃO BASTA. Dizer "apurado às 11:03" e não oferecer
        // atualizar deixa a pessoa com uma informação que ela não pode agir —
        // e, com o serviço escalando a zero, a tela fica aberta por horas. O
        // teste da falha foi quem achou este buraco: não havia caminho de
        // `pronto` de volta para uma nova leitura.
        <div className="flex items-center gap-3">
          <p className="text-xs text-gray-600 flex items-center gap-2">
            <Clock className="w-3 h-3" />
            {t('dashboard.apuradoEm', {
              // No fuso do painel, pelo mesmo motivo: o relógio ao lado dos
              // números tem de contar o tempo que os números contam.
              //
              // **`timeZoneName` NÃO É ENFEITE.** Sem ele sai "apurado às
              // 11:03" — e quem abre o painel de outro fuso lê isso como a hora
              // do próprio relógio, que é justamente a leitura errada. Com ele
              // sai "11:03:45 BRT", e o horário se declara. Achado na revisão.
              quando: new Date(dado.apurado_em).toLocaleTimeString('pt-BR', {
                timeZone: dado.fuso,
                timeZoneName: 'short',
              }),
            })}
          </p>
          <Button
            variant="link"
            size="sm"
            className="px-0 h-auto text-xs text-gray-500"
            onClick={rebuscar}
          >
            {t('dashboard.tentarDeNovo')}
          </Button>
        </div>
      )}
    </div>
  )
}
